// src/api/client.ts

import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import { joinUrl } from '../utils/url';
import { GATEWAY_BASE, SEC_13F_PATH, API_TIMEOUT_MS } from '../config/env';
import { notifyUnauthorized } from '../auth/unauthorizedHandler';

function apiBaseUrl(): string {
    return joinUrl(GATEWAY_BASE, SEC_13F_PATH);
}

export interface ApiClientOptions {
    timeoutMs?: number;
}

export function createApiClient(opts: ApiClientOptions = {}): AxiosInstance {
    const baseURL = apiBaseUrl();
    const timeout = opts.timeoutMs ?? API_TIMEOUT_MS;

    const api = axios.create({ baseURL, timeout, withCredentials: true });

    api.interceptors.request.use((config) => {
        config.headers = (config.headers ?? new AxiosHeaders()) as AxiosHeaders;
        return config;
    });

    api.interceptors.response.use(
        (r) => r,
        async (error) => {
            const status = error?.response?.status as number | undefined;
            const cfg = error?.config ?? {};
            const tries = (cfg.__retryCount ?? 0) as number;

            // >>> NEW: if backend says 401, force logout immediately
            if (status === 401) {
                notifyUnauthorized();
                // We still reject so callers see the failure if they care,
                // but user will already be logged out.
                return Promise.reject(error);
            }

            // Retry logic for 429 / 5xx
            const canRetry =
                status &&
                (status === 429 || status >= 500) &&
                tries < 2;

            if (canRetry) {
                await new Promise((res) => setTimeout(res, 400 * (tries + 1)));
                (cfg as any).__retryCount = tries + 1;
                return api(cfg);
            }

            // default: just fail
            return Promise.reject(error);
        }
    );

    return api;
}
